import { motion } from "framer-motion";
import { HERO, BRAND } from "@/content";
import { ArrowDown, KeyRound } from "lucide-react";
import { WhatsAppGlyph, whatsappLink } from "@/components/WhatsApp";

export default function Hero() {
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section
      id="hero"
      data-testid="section-hero"
      className="relative min-h-[100svh] w-full overflow-hidden bg-brand-olive-deep"
    >
      <div className="absolute inset-0">
        <img
          src="/images/exterior-building.jpg"
          alt="تاون هاوس الشامخة - الواجهة الرئيسية للمشروع"
          className="h-full w-full object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-brand-ink/58" />
        <div className="absolute inset-0 bg-gradient-to-b from-brand-ink/82 via-brand-olive-deep/68 to-brand-ink/90" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(244,241,232,0.08)_0%,rgba(24,32,24,0.34)_38%,rgba(12,16,12,0.82)_84%)]" />
        <div className="absolute inset-0 bg-white/[0.025] backdrop-blur-[1px]" />
        <div className="absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-brand-cream to-transparent" />
      </div>

      <div className="relative z-10 mx-auto flex min-h-[100svh] max-w-7xl flex-col items-center justify-center px-6 pb-14 pt-24 text-center lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.85, ease: "easeOut" }}
          className="mx-auto max-w-4xl px-2 py-4 sm:px-6"
        >
          <div
            className="mt-6 inline-flex items-center justify-center rounded-full border border-white/32 bg-black/24 px-5 py-2 text-sm text-white shadow-[0_16px_44px_rgba(0,0,0,0.28)] backdrop-blur-sm"
            data-testid="hero-ornament"
          >
            {HERO.eyebrow}
          </div>

          <h1
            className="mt-5 text-5xl font-semibold leading-[1.03] text-white drop-shadow-[0_22px_54px_rgba(0,0,0,0.86)] sm:text-6xl lg:text-[6.4rem]"
            data-testid="hero-title"
          >
            {HERO.title}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, delay: 0.22 }}
            className="mb-3 mt-3 text-xl text-[#FFE6A7] drop-shadow-[0_12px_30px_rgba(0,0,0,0.82)] sm:text-2xl lg:text-3xl"
            data-testid="hero-subtitle"
          >
            {HERO.subtitle}
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, delay: 0.36 }}
            className="mx-auto mb-4 max-w-2xl text-base leading-relaxed text-white drop-shadow-[0_10px_24px_rgba(0,0,0,0.78)] sm:text-lg"
            data-testid="hero-description"
          >
            {HERO.description}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.48 }}
            className="mb-5 mt-2 inline-flex items-center gap-3 rounded-full border border-white/30 bg-black/24 px-4 py-2 text-white shadow-[0_16px_48px_rgba(0,0,0,0.28)] backdrop-blur-sm"
            data-testid="hero-scarcity"
          >
            <KeyRound size={15} className="text-[#FFE6A7]" strokeWidth={1.8} />
            <span className="text-sm sm:text-base">
              متبقي <span className="mx-1 font-bold text-[#FFE6A7]">{BRAND.unitsRemaining}</span> وحدات فقط من أصل {BRAND.unitsTotal}
            </span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, delay: 0.6 }}
            className="mx-auto flex w-full max-w-sm flex-col items-stretch justify-center gap-3 sm:max-w-none sm:flex-row sm:flex-wrap sm:items-center"
          >
            <button
              onClick={() => scrollTo("form")}
              className="group inline-flex w-full items-center justify-center gap-3 rounded-full bg-brand-cream px-7 py-3.5 text-base text-brand-ink shadow-xl shadow-black/25 transition-all duration-300 hover:-translate-y-0.5 hover:bg-white sm:w-auto"
              data-testid="hero-primary-cta"
            >
              {HERO.cta}
              <span className="inline-block transition-transform group-hover:-translate-x-1">←</span>
            </button>
            <a
              href={whatsappLink(null, "hero")}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex w-full items-center justify-center gap-3 rounded-full bg-[#25D366] px-6 py-3.5 text-base text-white shadow-xl shadow-black/25 transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#1eb958] sm:w-auto"
              data-testid="hero-whatsapp-cta"
            >
              <WhatsAppGlyph size={19} />
              تواصل عبر واتساب
            </a>
            <button
              onClick={() => scrollTo("about")}
              className="inline-flex w-full items-center justify-center gap-3 rounded-full border border-white/46 bg-black/18 px-6 py-3.5 text-base text-white backdrop-blur-sm transition-all duration-300 hover:bg-white/18 sm:w-auto"
              data-testid="hero-secondary-cta"
            >
              {HERO.ctaSecondary}
            </button>
          </motion.div>
        </motion.div>

        <motion.button
          onClick={() => scrollTo("about")}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1 }}
          className="absolute inset-x-0 bottom-7 mx-auto flex w-fit items-center gap-2 text-sm text-brand-cream drop-shadow-[0_8px_18px_rgba(0,0,0,0.55)] hover:text-[#FFE6A7]"
          data-testid="hero-scroll-indicator"
        >
          <span>اكتشف المزيد</span>
          <ArrowDown size={16} className="animate-bounce" />
        </motion.button>
      </div>
    </section>
  );
}
